// PRE RMA — Liste des patients saisis : fusion de l'état serveur (ou du cache) et de la file d'attente locale.
//
// Pourquoi : un patient saisi hors connexion n'existe pas encore sur le serveur ; sans cette fusion il n'apparaissait pas dans la
// liste, n'était pas pris en compte pour le « prochain N° » (risque de doublon de numéro) et ne pouvait être ni corrigé ni supprimé.
// Fonction PURE (testée) : mêmes entrées → même liste, aucun accès réseau ni stockage.
import type { ConsultationInput } from './validation.ts';

export interface PatientServeur extends ConsultationInput { id: string; version: number }
export type OperationFile = 'CREATE' | 'UPDATE' | 'DELETE' | 'DELETE_ALL';
export type StatutFile = 'PENDING' | 'SYNCED' | 'ERROR' | 'CONFLICT';
export interface ElementFile {
  localId: string; operation: OperationFile; periodeId: string; consultationId?: string; baseVersion?: number;
  payload: Record<string, unknown>; syncStatus: StatutFile; serverId?: string; serverVersion?: number; erreur?: string; createdAt: number;
}

export interface Patient {
  /** Clé stable d'affichage : id serveur, sinon identifiant local. */
  cle: string;
  id?: string;        // identifiant serveur (absent tant que la création n'est pas synchronisée)
  localId?: string;   // identifiant local (patient saisi sur cet appareil)
  version?: number;
  data: ConsultationInput;
  /** Une opération (création / modification / suppression) attend encore l'envoi ou a échoué. */
  enAttente: boolean;
  statut?: 'ATTENTE' | 'ERREUR' | 'CONFLIT';
  message?: string;
  /** Date de saisie sur CET appareil (absent pour un patient venu du serveur sans trace locale). */
  creeLe: number | null;
}

const CHAMPS: Array<keyof ConsultationInput> = ['numeroRceBrut', 'casFievre', 'tdrResultat', 'ageBrut', 'genre', 'diagnostics', 'typeTraumatisme', 'traitementPalu', 'indicateurCrenas', 'admissionCrenas'];
export function donneesDepuisPayload(payload: Record<string, unknown>): ConsultationInput {
  const out: Record<string, unknown> = {};
  for (const k of CHAMPS) out[k] = payload[k] ?? (k === 'diagnostics' ? [] : k === 'numeroRceBrut' ? '' : null);
  return out as unknown as ConsultationInput;
}
const statutDe = (s: StatutFile): Patient['statut'] => (s === 'PENDING' ? 'ATTENTE' : s === 'ERROR' ? 'ERREUR' : s === 'CONFLICT' ? 'CONFLIT' : undefined);

export function fusionnerPatients(serveur: PatientServeur[], file: ElementFile[], debutSession: number): { patients: Patient[]; session: Patient[] } {
  const parId = new Map<string, Patient>();
  const ordre: Patient[] = [];
  for (const s of serveur) {
    const { id, version, ...data } = s;
    const p: Patient = { cle: id, id, version, data: donneesDepuisPayload(data as Record<string, unknown>), enAttente: false, creeLe: null };
    parId.set(id, p); ordre.push(p);
  }
  const masques = new Set<string>(); // patients dont la suppression est en attente d'envoi
  const elements = [...file].sort((a, b) => a.createdAt - b.createdAt);

  for (const it of elements) {
    if (it.operation === 'CREATE') {
      if (it.syncStatus === 'SYNCED' && it.serverId) {
        const p = parId.get(it.serverId);
        if (p) { p.creeLe = it.createdAt; p.localId = it.localId; }
        else { // liste serveur pas encore rafraîchie : on affiche quand même le patient synchronisé
          const n: Patient = { cle: it.serverId, id: it.serverId, localId: it.localId, version: it.serverVersion, data: donneesDepuisPayload(it.payload), enAttente: false, creeLe: it.createdAt };
          parId.set(it.serverId, n); ordre.push(n);
        }
      } else {
        const n: Patient = { cle: it.localId, localId: it.localId, data: donneesDepuisPayload(it.payload), enAttente: true, statut: statutDe(it.syncStatus), message: it.erreur, creeLe: it.createdAt };
        ordre.push(n);
      }
    }
  }
  for (const it of elements) {
    if (it.operation === 'UPDATE' && it.syncStatus !== 'SYNCED' && it.consultationId) {
      const p = parId.get(it.consultationId);
      if (p) { p.data = { ...p.data, ...donneesDepuisPayload(it.payload) }; p.enAttente = true; p.statut = statutDe(it.syncStatus); p.message = it.erreur; }
    } else if (it.operation === 'DELETE' && it.syncStatus !== 'SYNCED' && it.consultationId) {
      const p = parId.get(it.consultationId);
      if (!p) continue;
      if (it.syncStatus === 'PENDING') masques.add(it.consultationId); // supprimé pour l'utilisateur, en attente d'envoi
      else { p.enAttente = true; p.statut = statutDe(it.syncStatus); p.message = it.erreur ?? 'Suppression non appliquée.'; } // refus du serveur : le patient reste visible
    }
  }
  const patients = ordre.filter((p) => !(p.id && masques.has(p.id)));
  return { patients, session: patients.filter((p) => p.creeLe !== null && p.creeLe >= debutSession) };
}
